import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import assetRopaApi from 'config/api/assetRopa/assetRopa';
import type { AssetProcessingActivityDetail } from 'config/api/assetRopa/assetRopa.types';
import invalidateOwnerAssetRopaQueries from 'config/api/assetRopa/assetRopaQuery.helpers';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';
import type { AssetProcessingApaFormValues } from 'pages/ProcessingActivities/_shared/assetProcessingApa.types';

import {
  isAssetProcessingActivityRevisionConflict,
  isAssetProcessingActivityRevisionValidationError,
  mapDetailToFormValues,
  mapFormValuesToSaveInput,
} from './assetProcessingActivityEditor.helpers';

type UseAssetProcessingActivityEditorOptions = {
  apaId: number;
  assetRopaId: number;
  initialEditMode?: boolean;
  onConflict?: () => void;
  onNavigate: () => void;
  onRevisionIntegrationError?: () => void;
  onSaveSuccess?: (detail: AssetProcessingActivityDetail, closeAfterSave: boolean) => void;
  onSubmitSuccess?: (detail: AssetProcessingActivityDetail) => void;
  organizationId?: number;
};

const useAssetProcessingActivityEditor = ({
  apaId,
  initialEditMode = false,
  onConflict,
  onNavigate,
  onRevisionIntegrationError,
  onSaveSuccess,
  onSubmitSuccess,
  organizationId,
}: UseAssetProcessingActivityEditorOptions) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [formValues, setFormValues] = useState<AssetProcessingApaFormValues | null>(null);
  const [clarificationResponse, setClarificationResponse] = useState('');
  const clarificationResponseRef = useRef(clarificationResponse);
  clarificationResponseRef.current = clarificationResponse;
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});

  const detailQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId],
    async () => {
      const { data } = await assetRopaApi.getProcessingActivity(organizationId as number, apaId);
      return data;
    },
    {
      enabled: Boolean(organizationId),
      retry: false,
    },
  );

  useEffect(() => {
    if (detailQuery.data) {
      setFormValues(mapDetailToFormValues(detailQuery.data));
      setIsDirty(false);
      setValidationErrors({});
    }
  }, [detailQuery.data]);

  const reviewId = detailQuery.data?.review?.id;
  const reviewStatus = detailQuery.data?.review?.status;
  const reviewClarificationComment = detailQuery.data?.review?.clarificationComment;

  useEffect(() => {
    setClarificationResponse(detailQuery.data?.review?.clarificationResponse ?? '');
  }, [detailQuery.data?.review?.clarificationResponse, reviewClarificationComment, reviewId, reviewStatus]);

  const handleFormChange = useCallback((nextValues: AssetProcessingApaFormValues) => {
    setFormValues(nextValues);
    setIsDirty(true);
    setValidationErrors({});
  }, []);

  const handleClarificationResponseChange = useCallback((nextValue: string) => {
    setClarificationResponse(nextValue);
    setValidationErrors(current => {
      if (!current.clarificationResponse) {
        return current;
      }

      const rest = { ...current };
      delete rest.clarificationResponse;
      return rest;
    });
  }, []);

  const handleMutationError = useCallback(
    async (error: unknown) => {
      if (isAssetProcessingActivityRevisionConflict(error)) {
        await detailQuery.refetch();
        onConflict?.();
        return;
      }

      if (isAssetProcessingActivityRevisionValidationError(error)) {
        onRevisionIntegrationError?.();
      }
    },
    [detailQuery, onConflict, onRevisionIntegrationError],
  );

  const allowNextNavigationRef = useRef(() => {});

  const saveMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_APA_UPDATE,
    async (closeAfterSave: boolean) => {
      if (!formValues || !detailQuery.data || !organizationId) return null;

      const { data } = await assetRopaApi.saveProcessingActivity(
        organizationId,
        apaId,
        mapFormValuesToSaveInput(formValues, detailQuery.data),
      );

      return { closeAfterSave, data };
    },
    {
      onSuccess: result => {
        if (!result) return;

        queryClient.setQueryData([QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId], result.data);
        Promise.resolve(invalidateOwnerAssetRopaQueries(queryClient, organizationId)).catch(() => undefined);
        setFormValues(mapDetailToFormValues(result.data));
        setIsDirty(false);
        if (result.closeAfterSave) {
          allowNextNavigationRef.current();
        }
        onSaveSuccess?.(result.data, result.closeAfterSave);
      },
      onError: (error: unknown) => {
        handleMutationError(error).catch(() => undefined);
      },
    },
  );

  const submitMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_APA_SUBMIT,
    async (revision: number) => {
      if (!detailQuery.data || !organizationId) return null;

      const { data } = await assetRopaApi.submitProcessingActivity(organizationId, apaId, {
        updatedAt: detailQuery.data.updatedAt,
        revision,
        ...(detailQuery.data.review?.status === 'clarification_requested'
          ? { clarificationResponse: clarificationResponseRef.current }
          : {}),
      });

      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.ASSET_ROPA_APA_DETAIL, organizationId, apaId], data);
        Promise.resolve(invalidateOwnerAssetRopaQueries(queryClient, organizationId)).catch(() => undefined);
        setIsDirty(false);
        allowNextNavigationRef.current();
        onSubmitSuccess?.(data);
      },
      onError: (error: unknown) => {
        handleMutationError(error).catch(() => undefined);
      },
    },
  );

  const handleSubmit = useCallback(async () => {
    let revision = detailQuery.data?.revision;
    if (revision == null) return;

    if (isDirty) {
      const saved = await saveMutation.mutateAsync(false);
      revision = saved?.data.revision ?? revision;
    }

    await submitMutation.mutateAsync(revision);
  }, [detailQuery.data?.revision, isDirty, saveMutation, submitMutation]);

  const { NavigationPrompt, allowNextNavigation, handleNavigation } = useUnsavedChangesProtection({
    enabled: isEditing,
    isDirty,
    onNavigate,
    onSave: async () => {
      await saveMutation.mutateAsync(false);
    },
  });

  allowNextNavigationRef.current = allowNextNavigation;

  const readOnly = !isEditing || detailQuery.data?.review?.status === 'submitted' || detailQuery.data?.review?.status === 'accepted';

  return {
    clarificationResponse,
    detail: detailQuery.data,
    formValues,
    handleClarificationResponseChange,
    handleFormChange,
    handleNavigation,
    handleSubmit,
    isDirty,
    isEditing,
    isError: detailQuery.isError,
    isLoading: detailQuery.isLoading,
    NavigationPrompt,
    readOnly,
    refetch: detailQuery.refetch,
    saveMutation,
    setIsEditing,
    setValidationErrors,
    submitMutation,
    validationErrors,
  };
};

export default useAssetProcessingActivityEditor;
